import { useCallback, useMemo } from 'react';
import { useDENTContext } from '../../../Services/DENTContext';
import {
	Tooth,
	ToothRestoration,
	ToothSurface,
} from '../../../../../graphql/graphql';
import { SurfaceOperation } from '../../../Enums/SurfaceOperation.enum';
import { handleSurfacesOperation } from '../../../Helpers/surfaces/toothSurfaces.helper';
import getToothState from '../../../Helpers/getToothState';
import useToothRestorationFillings from './useToothRestorationFillings';

export default function useToothRestoration() {
	const { activeTooth, setActiveTooth } = useDENTContext();
	const restoration = activeTooth?.restoration;

	const setRestoration = useCallback(
		(restoration: ToothRestoration) => {
			setActiveTooth((prev: Tooth | undefined) => {
				if (!prev) return prev;

				const tooth = {
					...prev,
					restoration: {
						...restoration,
						surfaces: prev.restoration?.surfaces ?? [],
					},
				};

				return {
					...tooth,
					state: getToothState(tooth),
				};
			});
		},
		[setActiveTooth],
	);

	const setSurfaces = useCallback(
		(surfaces: ToothSurface[], operation: SurfaceOperation) => {
			setActiveTooth((prev: Tooth | undefined) => {
				if (!prev) return prev;

				return {
					...prev,
					restoration: {
						...prev.restoration,
						surfaces: handleSurfacesOperation(
							prev.restoration?.surfaces ?? [],
							surfaces,
							operation,
						),
					},
				};
			});
		},
		[setActiveTooth],
	);

	const { fillings } = useToothRestorationFillings({
		restoration,
		setRestoration,
	});

	return useMemo(
		() => ({
			restoration: {
				data: restoration,
				setRestoration,
				surfaces: restoration?.surfaces,
				setSurfaces,
				fillings,
			},
		}),
		[activeTooth, restoration],
	);
}
